import { ProviderActionRule } from "@app/projects/network-assessment/provider-actions/interfaces";
import { RecordingCriteriaEnum } from "@app/projects/enumerators";
import { ProcessStatusEnum, ProviderActionsTabsEnum } from "@app/projects/network-assessment/provider-actions/enumerators";

// Shared constants
export const netNewDisplayText = "Net New";
export const ALL_SPECIALTIES = "All Specialties";
export const ALL_OPTIONS_TEXT = "All";
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];
export const DEFAULT_RECORDS_PER_PAGE = 50;
export const ORGANIZATION_ACTIONS_UNAVAILABLE_MESSAGE = "Organization actions are temporarily unavailable. Actions at lower granularities (Contract Entity, Provider, Access Point) are still available.";

export const getTabText = (tab: ProviderActionsTabsEnum): string => {
  switch (tab) {
    case ProviderActionsTabsEnum.Targeted:
      return "Target";
    case ProviderActionsTabsEnum.Terminated:
      return "Termination";
    case ProviderActionsTabsEnum.Excluded:
      return "Exclusion";
    default:
      return "Target";
  }
}

export const getRemainActionText = (tab: ProviderActionsTabsEnum): string => {
  switch (tab) {
    case ProviderActionsTabsEnum.Targeted:
      return "targeted";
    case ProviderActionsTabsEnum.Terminated:
      return "terminated";
    case ProviderActionsTabsEnum.Excluded:
      return "excluded";
    default:
      return "targeted";
  }
}

export const getProcessedActionText = (tab: ProviderActionsTabsEnum): string => {
  switch (tab) {
    case ProviderActionsTabsEnum.Terminated:
      return "Restore";
    default:
      return "Remove";
  }
}

export const getRecordingCriteriaDisplay = (isNetNew: boolean, byContractEntity: boolean, byNPI: boolean, byTIN: boolean, byNPI2?: boolean, byCCN?: boolean): string => {
  if (isNetNew) {
    return netNewDisplayText;
  }

  if (byCCN) {
    return RecordingCriteriaEnum.CCN;
  }

  if (byNPI2) {
    return RecordingCriteriaEnum.NPI2;
  }

  if (byContractEntity) {
    return RecordingCriteriaEnum.ContractEntity.toString();
  }

  if (byNPI && byTIN) {
    return RecordingCriteriaEnum.NPIAndTIN.toString();
  } else if (byNPI) {
    return RecordingCriteriaEnum.NPI.toString();
  } else if (byTIN) {
    return RecordingCriteriaEnum.TIN.toString();
  }

  return RecordingCriteriaEnum.Name.toString();
};

export const getRuleRecordingCriteria = (rule: ProviderActionRule): string => "providerTerminationProjectId" in rule
  ? getRecordingCriteriaDisplay(false, rule.terminateByContractEntity, rule.terminateByNPI, rule.terminateByTIN, rule.terminateByNPI2, rule.terminateByCCN)
  : getRecordingCriteriaDisplay("netNewProviderProjectId" in rule, rule.projectedByContractEntity, rule.projectedByNPI, rule.projectedByTIN, rule.projectedByNPI2, rule.projectedByCCN);

export const isOrganizationRule = (rule: ProviderActionRule): boolean => getRuleRecordingCriteria(rule) === RecordingCriteriaEnum.NPI2;

export function extractSpecialtyCodes(
rules: ProviderActionRule[]
): { hasAllSpecialtiesProvider: boolean; specialties: Array<{ code: string; name: string }>; } {

  const specialtyMap = new Map<string, string>(); // code -> name
  let hasAllSpecialtiesProvider = false;

  for (const rule of rules) {
    if (rule.allSpecialties) {
      hasAllSpecialtiesProvider = true;
    } else if (rule.specialtyCode && rule.specialtyName) {
      // Only add if not already in the map (ensures distinctness)
      if (!specialtyMap.has(rule.specialtyCode)) {
        specialtyMap.set(rule.specialtyCode, rule.specialtyName);
      }
    }
  }

  // Convert Map to Array
  const specialties = Array.from(specialtyMap, ([code, name]) => ({ code, name }));

  return { hasAllSpecialtiesProvider, specialties };
}

export const getSuccessToastMessage = (tab: ProviderActionsTabsEnum, status: ProcessStatusEnum, count: number): string => {
  const segment = getRemainActionText(tab);

  const verb =
    status === ProcessStatusEnum.Unprocessed
      ? "undone"
      : tab === ProviderActionsTabsEnum.Terminated
        ? "restored"
        : "removed";

  const rulePhrase = count === 1 ? "rule was" : "rules were";

  return `${count} ${segment} ${rulePhrase} ${verb}.`;
};